<template>
  <div class="home">
    <section class="hero">
      <div class="hero-grid"></div>
      <div class="hero-orbit orbit-one"></div>
      <div class="hero-orbit orbit-two"></div>
      <div class="container hero-inner">
        <div class="hero-content">
          <div class="eyebrow light">ENGINEERING SOLUTIONS · PORT HARCOURT</div>
          <h1>Powering.<br><span>Protecting.</span><br>Connecting.</h1>
          <p>Reliable power, fire protection, communication, security and IT systems engineered for homes, businesses and industrial facilities.</p>
          <div class="hero-actions">
            <router-link to="/contact" class="btn btn-red">Request a Consultation <span>→</span></router-link>
            <router-link to="/services" class="btn btn-outline-light">Explore Services</router-link>
          </div>
        </div>

        <div class="hero-visual">
          <div class="energy-card">
            <div class="energy-top"><span>FACILITY SYSTEMS</span><b>LIVE</b></div>
            <div class="energy-ring"><div><strong>24/7</strong><small>Protection</small></div></div>
            <div class="energy-stats">
              <div><span>POWER</span><b>ONLINE</b></div>
              <div><span>SECURITY</span><b>ACTIVE</b></div>
              <div><span>FIRE</span><b>READY</b></div>
            </div>
          </div>
        </div>
      </div>
      <div class="hero-scroll">SCROLL TO EXPLORE <span>↓</span></div>
    </section>

    <section class="intro section">
      <div class="container intro-grid">
        <div>
          <span class="eyebrow">ONE ENGINEERING PARTNER</span>
          <h2>Systems that keep your facility running.</h2>
        </div>
        <div class="intro-copy">
          <p>From solar and backup power to fire protection, communications and electronic security, we design and install critical systems around the way your facility actually operates.</p>
          <router-link to="/about" class="text-link">Why VoltEdge <span>→</span></router-link>
        </div>
      </div>
    </section>

    <section class="services-section section section-soft">
      <div class="container">
        <SectionHeading
          eyebrow="WHAT WE DO"
          title="Integrated solutions. One team."
          text="Technical systems designed to work independently or together as part of a complete facility infrastructure."
        />
        <div class="services-grid">
          <ServiceCard
            number="01"
            title="Power Backup"
            description="Keep essential operations running when the grid cannot."
            :items="['Solar power systems', 'Inverters & battery storage', 'UPS systems', 'Hybrid power solutions']"
            :icon="icons.power"
            link="/services#power"
          />
          <ServiceCard
            number="02"
            title="Fire Protection"
            description="Protect people, property and critical infrastructure."
            :items="['Fire hydrant & hose reel', 'Smoke detection', 'Fire extinguishers', 'Fire blankets']"
            :icon="icons.fire"
            link="/services#fire"
          />
          <ServiceCard
            number="03"
            title="Communication"
            description="Keep teams connected across your facility."
            :items="['Intercommunication', 'Radio communication', 'Message systems', 'Communication infrastructure']"
            :icon="icons.communication"
            link="/services#communication"
          />
          <ServiceCard
            number="04"
            title="Security Systems"
            description="See more, detect faster and respond sooner."
            :items="['CCTV surveillance', 'Electric fencing', 'Motion sensors', 'Perimeter protection']"
            :icon="icons.security"
            link="/services#security"
          />
          <ServiceCard
            number="05"
            title="IT Networking"
            description="Build a dependable digital infrastructure."
            :items="['Structured cabling', 'LAN/WAN infrastructure', 'Network installation', 'Office connectivity']"
            :icon="icons.network"
            link="/services#networking"
          />
        </div>
      </div>
    </section>

    <section class="feature-section section">
      <div class="container feature-grid">
        <div class="feature-visual">
          <div class="feature-panel">
            <div class="panel-line"></div>
            <div class="panel-header"><span>ENGINEERED FOR</span><strong>YOUR FACILITY</strong></div>
            <div class="facility-map">
              <div class="map-node node-power"><span>POWER</span></div>
              <div class="map-node node-fire"><span>FIRE</span></div>
              <div class="map-node node-security"><span>SECURITY</span></div>
              <div class="map-node node-comms"><span>COMMS</span></div>
              <div class="map-core">CORE<br>SYSTEM</div>
            </div>
          </div>
        </div>
        <div class="feature-copy">
          <span class="eyebrow">BUILT AROUND YOUR FACILITY</span>
          <h2>Not a package. A system designed for you.</h2>
          <p>Every facility has different loads, risks, workflows and operating conditions. We assess what matters, design around it and install the right combination of systems.</p>
          <div class="feature-list">
            <div><span>01</span><strong>Site assessment</strong><p>Understand the building, equipment and operational requirements.</p></div>
            <div><span>02</span><strong>Engineering design</strong><p>Develop a solution based on real site requirements.</p></div>
            <div><span>03</span><strong>Installation & commissioning</strong><p>Install, test and hand over a system ready for use.</p></div>
          </div>
          <router-link to="/contact" class="btn btn-navy">Discuss your facility <span>→</span></router-link>
        </div>
      </div>
    </section>

    <section class="industries section section-navy">
      <div class="container">
        <SectionHeading eyebrow="WHERE WE WORK" title="Solutions for the places that matter." text="From residential properties to commercial and industrial facilities." />
        <div class="industry-grid">
          <div v-for="(industry, i) in industries" :key="industry.name" class="industry-item">
            <span>0{{ i + 1 }}</span><strong>{{ industry.name }}</strong><small>{{ industry.detail }}</small>
          </div>
        </div>
      </div>
    </section>

    <section class="projects-section section">
      <div class="container">
        <div class="projects-heading">
          <SectionHeading eyebrow="SELECTED WORK" title="Built to perform." text="A preview of the systems we deliver." />
          <router-link to="/projects" class="text-link">View all projects <span>→</span></router-link>
        </div>
        <div class="projects-grid">
          <ProjectCard category="POWER BACKUP" location="Port Harcourt" title="Commercial Hybrid Solar System" tone="blue" />
          <ProjectCard category="SECURITY" location="Rivers State" title="Estate CCTV & Perimeter Protection" tone="red" />
          <ProjectCard category="FIRE PROTECTION" location="Port Harcourt" title="Hydrant & Hose Reel System" tone="dark" />
        </div>
      </div>
    </section>

    <section class="cta-section">
      <div class="container cta-inner">
        <div><span class="eyebrow light">LET'S BUILD IT RIGHT</span><h2>Have a facility to power, protect or connect?</h2></div>
        <router-link to="/contact" class="btn btn-red">Start a Conversation <span>→</span></router-link>
      </div>
    </section>
  </div>
</template>

<script>
import SectionHeading from '../components/SectionHeading.vue'
import ServiceCard from '../components/ServiceCard.vue'
import ProjectCard from '../components/ProjectCard.vue'

export default {
  name: 'Home',
  components: { SectionHeading, ServiceCard, ProjectCard },
  data() {
    return {
      industries: [
        { name: 'Residential', detail: 'Homes · Estates · Apartments' },
        { name: 'Commercial', detail: 'Offices · Retail · Complexes' },
        { name: 'Industrial', detail: 'Factories · Warehouses · Facilities' },
        { name: 'Hospitality', detail: 'Hotels · Restaurants · Events' },
        { name: 'Healthcare', detail: 'Hospitals · Clinics · Labs' },
        { name: 'Institutions', detail: 'Schools · Churches · Public facilities' }
      ],
      icons: {
        power: '<svg viewBox="0 0 48 48" fill="none"><path d="M27 4 10 27h13l-2 17 17-24H25l2-16Z" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round"/></svg>',
        fire: '<svg viewBox="0 0 48 48" fill="none"><path d="M25 44c9 0 15-6 15-14 0-8-5-13-10-18 0 7-3 10-6 11 1-8-3-14-9-19 1 9-6 14-6 25 0 9 7 15 16 15Z" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round"/></svg>',
        communication: '<svg viewBox="0 0 48 48" fill="none"><rect x="15" y="5" width="18" height="38" rx="3" stroke="currentColor" stroke-width="2.5"/><path d="M20 11h8M20 37h8M8 17c-3 2-5 5-5 9s2 7 5 9M40 17c3 2 5 5 5 9s-2 7-5 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>',
        security: '<svg viewBox="0 0 48 48" fill="none"><path d="M24 5 40 11v11c0 10-6 17-16 21C14 39 8 32 8 22V11l16-6Z" stroke="currentColor" stroke-width="2.5"/><circle cx="24" cy="21" r="5" stroke="currentColor" stroke-width="2.5"/><path d="M15 34c2-4 5-6 9-6s7 2 9 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>',
        network: '<svg viewBox="0 0 48 48" fill="none"><rect x="18" y="4" width="12" height="10" rx="2" stroke="currentColor" stroke-width="2.5"/><rect x="4" y="34" width="12" height="10" rx="2" stroke="currentColor" stroke-width="2.5"/><rect x="32" y="34" width="12" height="10" rx="2" stroke="currentColor" stroke-width="2.5"/><path d="M24 14v10M10 34v-6h28v6M24 24v4" stroke="currentColor" stroke-width="2.5"/></svg>'
      }
    }
  }
}
</script>